import {Box} from "@mui/material";
import {useEffect, useRef, useState} from "react";

import videojs from "video.js";
import 'video.js/dist/video-js.css';
import {useTheme} from "../../../contexts/theme";
import {FocusElement, Landmark, TrimPoint} from "../../../types/editor";
import {AspectRatio} from "../../../types/generic";
import "../../EditorPopup/customVideoJS.css"
import 'video.js/dist/video-js.css';


interface WindowPlayerProps {
    url: string;
    setVideoTime?: (time: number) => void
    setVideoDuration?: (time: number) => void
    frameRate: number,
    changeVideoTime?: number
    isPlaying: boolean
    muted: boolean
    addLandMark?: (landMark: Landmark) => void
    aspectRatio: AspectRatio,
    windowCoords: number[],
    isActiveTime: boolean,
    currentFocusElement?: FocusElement
    setIsPlaying?: (isPlaying: boolean) => void
    moveTrimPoint ?: (amount: number, direction: number, index: number, type: 'start' | 'end', shiftKeyPressed: boolean) => void
    setVideoRef?: (video: HTMLVideoElement) => void
    isWindowUpdating?: boolean
    draggableWindow?: boolean
    onCanPlay?: () => void
    onEnded?: () => void
    loop?: boolean
    trimPoints?: TrimPoint[]
    shortcuts: boolean
    dispose?: boolean
}

const WindowPlayer: React.FC<WindowPlayerProps> = ({dispose=false, shortcuts,trimPoints, onEnded = () => {},loop = false, draggableWindow = true,isWindowUpdating, setVideoRef, moveTrimPoint, setIsPlaying, currentFocusElement, isActiveTime, windowCoords, aspectRatio, url, isPlaying, addLandMark, muted, setVideoTime = (time: number) => {}, setVideoDuration = (time: number) => {}, frameRate, changeVideoTime, onCanPlay = () => {}}) => {

    const videoRef = useRef<HTMLVideoElement>(null);
    const playerRef = useRef<any>(null);
    const frameRateInterval = useRef<NodeJS.Timer | undefined>(undefined);
    const aspectRatioRef = useRef<AspectRatio>();
    const frameRateRef = useRef<number>();
    const coordsRef = useRef<number[]>([]);
    const focusRef = useRef<FocusElement>();
    const trimPointsRef = useRef<TrimPoint[]>([])

    const theme = useTheme();

    const [windowHeight, setWindowHeight] = useState<number>(0);
    const [windowOffset, setWindowOffset] = useState<number>(0);
    const [windowWidth, setWindowWidth] = useState<number>(0);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [dragStart, setDragStart] = useState<number>(0);


    useEffect(() => {
        if(trimPoints){
            trimPointsRef.current = trimPoints
        }
    }, [trimPoints]);

    useEffect(() => {
        if (changeVideoTime!==undefined && playerRef.current){
            playerRef.current!.currentTime(changeVideoTime);
            computeWindow();
        }
    }, [changeVideoTime]);

    useEffect(() => {
        if (!isPlaying){
            playerRef.current?.pause();
        }else{
            playerRef.current?.play();
        }
    }, [isPlaying]);

    useEffect(() => {
        if (!muted){
            playerRef.current?.muted(true);
        }else{
            playerRef.current?.muted(false);
        }
    }, [muted]);

    const initPlayer = () => {
        if (videoRef && videoRef.current && url && !playerRef.current) {
            playerRef.current = videojs(videoRef.current, {
                controls: false,
                preload: 'auto',
                muted: false,
                sources: [{
                    src: url,
                    type: 'video/mp4'
                }]
            });
            playerRef.current.on('canplaythrough', onCanPlay)
            playerRef.current.on('loadedmetadata', () => setVideoDuration(playerRef.current.duration()));
            playerRef.current.on('stop', () => {clearInterval(frameRateInterval.current); frameRateInterval.current = undefined;});
            playerRef.current.on('pause', () => {clearInterval(frameRateInterval.current); frameRateInterval.current = undefined;});
            playerRef.current.on('ended', () => {
                if (setIsPlaying && !loop)
                    setIsPlaying(false);
                if (setIsPlaying && loop){
                    playerRef.current.play();
                    setIsPlaying(true);
                }
                onEnded()
            });
            playerRef.current.on('play', () => {
                const intervalId = setInterval(() => {
                    if(trimPointsRef.current && trimPointsRef.current.length > 0){
                        if(playerRef.current.currentTime() < trimPointsRef.current[0].start){
                            playerRef.current.currentTime(trimPointsRef.current[0].start)
                        }
                    }
                    setVideoTime(playerRef.current.currentTime());
                    computeWindow();
                }, (1/frameRate) * 1000);
                frameRateInterval.current= intervalId;
            });
        }
    }

    useEffect(() => {
        if (videoRef && videoRef.current && !playerRef.current) {
            initPlayer();
        }
        if (videoRef && videoRef.current) {
            setWindowHeight(videoRef.current.clientHeight);
        }
        if (setVideoRef && videoRef.current) {
            setVideoRef(videoRef.current);
        }
    }, [videoRef]);

    useEffect(initPlayer, [url]);


    function windowDrag(e:any){
        if(isDragging){
            let windowOffset = (e.clientX - dragStart)
            if (windowOffset < 0){
                windowOffset = 0;
            }else if (windowOffset > videoRef!.current!.clientWidth - windowWidth){
                windowOffset = videoRef.current!.clientWidth - windowWidth;
            }
            setWindowOffset(windowOffset);
        }

    }

    function windowGrabEnd(){
        setIsDragging(false)
        if (aspectRatio.label!=='16:9' && playerRef.current.paused()){
            const landmark = getNewLandmark();
            if (addLandMark){
                addLandMark(landmark);
            }
        }
    }


    function windowGrabStart(e: any){
        if (playerRef.current.paused() && !isWindowUpdating && draggableWindow){
            setDragStart(e.clientX - windowOffset);
            setIsDragging(true);
        }
    }

    useEffect(() => {
        const videoContainer = videoRef.current;
        if (!videoContainer) return;
        const handleResize = () => {
            const newHeight = videoContainer.clientHeight;
            if (newHeight !== windowHeight) {
                setWindowHeight(newHeight);
            }
        };
        const resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(videoContainer);
        computeWindow()
        return () => resizeObserver.disconnect();
    }, [windowHeight]);

    function computeWindow(){
        if (aspectRatioRef.current && playerRef.current && videoRef.current){
            const videoTime = playerRef.current!.currentTime();
            const videoFrame = Math.floor(videoTime * (frameRateRef.current as number));
            const windowCentreNorm = coordsRef.current[videoFrame] ? coordsRef.current[videoFrame] : 0.5;
            const windowCentre =  windowCentreNorm * videoRef.current!.clientWidth;
            const windowWidth = videoRef.current!.clientHeight * aspectRatioRef.current!.numeric;
            let windowOffset = windowCentre - (windowWidth / 2);
            if (windowOffset > videoRef.current!.clientWidth - windowWidth){
                windowOffset = videoRef.current!.clientWidth - windowWidth;
            }
            if (windowOffset < 0) {
                windowOffset = 0;
            }
            setWindowOffset(windowOffset);
            setWindowWidth(windowWidth);
            /*console.log({
                videoTime,
                videoFrame,
                windowCentreNorm,
                windowCentre,
                windowWidth,
                windowOffset,
                windowHeight,
                frameRate: frameRateRef.current,
                coord: coordsRef.current[videoFrame],
                width: videoRef.current!.clientWidth,
                height: videoRef.current!.clientHeight
            });*/
        }
    }

    useEffect(() => {
        aspectRatioRef.current = aspectRatio
        computeWindow()
    }, [aspectRatio]);

    useEffect(() => {
        frameRateRef.current = frameRate
    }, [frameRate]);

    useEffect(() => {
        coordsRef.current = windowCoords;
        computeWindow();
    }, [windowCoords]);

    useEffect(() => {
        focusRef.current = currentFocusElement
    }, [currentFocusElement]);

    useEffect(() => {
        if(dispose && playerRef.current){
            playerRef.current.pause()
            playerRef.current.dispose();
            playerRef.current = null;
        }
        if (dispose && frameRateInterval.current){
            clearInterval(frameRateInterval.current)
            frameRateInterval.current = undefined
        }
    }, [dispose]);

    function getNewLandmark(): Landmark{
        const currentVideoTime = playerRef.current!.currentTime();
        const currentVideoFrame = Math.floor(currentVideoTime * frameRate);
        const currentCenter = (windowOffset + windowWidth / 2) / videoRef.current!.clientWidth
        return {
            time: currentVideoTime,
            frame: currentVideoFrame,
            offset: currentVideoTime / playerRef.current.duration(),
            coordinate: currentCenter
        };
    }

    function moveWindow(amount: number, direction: 1|-1){
        setWindowOffset((prevWindowOffset) =>{
            let newOffset = prevWindowOffset + (amount * direction);
            newOffset = newOffset < 0 ? 0 : newOffset;
            newOffset = newOffset > videoRef.current!.clientWidth - windowWidth ? videoRef.current!.clientWidth - windowWidth : newOffset;
            return newOffset
        });
    }

    function movePlayerBar(amount: number, direction: number){
        let videoTime = playerRef.current!.currentTime();
        videoTime = videoTime + (amount * direction);
        videoTime = videoTime < 0 ? 0 : videoTime;
        videoTime = videoTime > playerRef.current.duration() ? playerRef.current.duration() : videoTime;
        playerRef.current!.currentTime(videoTime);
        setVideoTime(videoTime);
    }

    function changePlayState(){
        //console.log("Ho fatto play con spazio", {isPlaying, trimPoints: trimPointsRef.current, internal: playerRef.current.pause()});
        if (playerRef.current.paused()){
            if (trimPointsRef.current && trimPointsRef.current.length > 0){
                const lastPossibleTrimPoints =  trimPointsRef.current[trimPointsRef.current.length - 1];
                const endTime = Math.trunc(lastPossibleTrimPoints.end * 1000) / 1000;
                const startTime = trimPointsRef.current[0].start
                const videoTime = Math.trunc(playerRef.current.currentTime() * 1000) / 1000;
                if (endTime === videoTime){
                    playerRef.current.currentTime(startTime)
                }
                //console.log("Ho fatto play con spazio", {trimPoints: trimPointsRef.current, endTime, videoTime})
            }
            playerRef.current.play();
            if (setIsPlaying){
                setIsPlaying(true);
            }
        }else{
            playerRef.current.pause();
            if (setIsPlaying){
                setIsPlaying(false);
            }
        }
    }



    function handleWindowKeyDown(event: KeyboardEvent){
        //If press space change the playing state
        if(shortcuts){
            if (event.key === " "){
                    changePlayState();

            }
            //If press control move the window
            else if (event.ctrlKey){
                if (event.key === 'ArrowRight' || event.key === 'ArrowLeft'){
                    const direction = event.key === 'ArrowRight' ? 1 : -1;
                    const amount = event.shiftKey ? 10 : 1;
                    moveWindow(amount, direction);
                }
            }else if(event.key === 'ArrowRight' || event.key === 'ArrowLeft'){
                //If no grab are selected move the player bar
                if (focusRef.current!.type !== 'startGrabber' && focusRef.current!.type !== 'endGrabber'){
                    const direction = event.key === 'ArrowRight' ? 1 : -1;
                    const amount = event.shiftKey ? 10/(frameRateRef.current as number) : 1/(frameRateRef.current as number);
                    movePlayerBar(amount, direction);
                    computeWindow()
                    //Else move the selected Grab
                }else{
                    if (moveTrimPoint){
                        const trimPointType = focusRef.current!.type === 'startGrabber' ? 'start' : 'end';
                        const direction = event.key === 'ArrowRight' ? 1 : -1;
                        const amount = event.shiftKey ? 10/(frameRateRef.current as number) : 1/(frameRateRef.current as number);
                        moveTrimPoint(amount, direction, focusRef.current!.index, trimPointType, event.shiftKey);
                    }
                }
            }
        }
    }

    useEffect(() => {
        window.addEventListener('keydown', handleWindowKeyDown);

        return () => {
            window.removeEventListener('keydown', handleWindowKeyDown);
        }
    }, [])

    function getWindowCursor(){
        if (!draggableWindow){
            return 'auto'
        }
        if (isDragging){
            return 'grabbing';
        }
        if (!isPlaying && !isWindowUpdating){
            return 'grab';
        }
        return 'auto';
    }

    return (
        <Box position="relative">
            <Box
                onMouseMove={windowDrag}
                sx={{
                width: '100%',
                height: windowHeight,
                zIndex: 10,
                position: 'absolute',
                top: '0',
                backgroundColor: 'transparent',
                overflowX: 'hidden',
            }}>
                <Box sx={{
                    width: windowOffset + 'px',
                    left: 0,
                    backgroundColor: !isActiveTime ? theme.palette.darkRed : theme.palette.lightBlack,
                    height: '100%',
                    zIndex: 11,
                    position: 'absolute',
                }}></Box>
                <Box onMouseDown={windowGrabStart} onMouseUp = {windowGrabEnd}
                     sx={{
                         left: windowOffset + 'px',
                         width: windowWidth + 'px',
                         backgroundColor: !isActiveTime ? theme.palette.lightRed : 'transparent',
                         position: 'absolute',
                         height: '100%',
                         zIndex: 11,
                         '&:hover': {cursor: getWindowCursor()},
                     }}>
                </Box>
                <Box sx={videoRef.current && videoRef.current.clientWidth ?
                    {
                        width: (videoRef.current.clientWidth - (windowOffset + windowWidth)) + 'px',
                        backgroundColor: !isActiveTime ? theme.palette.darkRed : theme.palette.lightBlack,
                        left: windowOffset + windowWidth,
                        position: 'absolute',
                        height: '100%'
                    } :
                    {}
                }
                >
                </Box>
            </Box>
            <video crossOrigin="anonymous" ref={videoRef} style={{width: '100%', height: 'auto'}}></video>
        </Box>
    )
        ;
};

export default WindowPlayer;